import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  TOAST_VARIANTS,
  formatRelative,
  notificationSubline,
  type AppointmentNotification,
  type AppointmentNotificationsState,
} from '../hooks/useAppointmentNotifications';

type InboxTab = 'notifications' | 'news';

// Ruhiges Zeichen für die Inbox-Liste: getönter Kreis statt der vollflächigen Toast-Icons.
function InboxGlyph({ type }: { type: AppointmentNotification['type'] }) {
  const color = TOAST_VARIANTS[type].bg;
  return (
    <span
      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ background: `${color}1A`, color }}
    >
      {type === 'booked' ? (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="5 12.5 10 17.5 19 7.5" />
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 6l12 12" /><path d="M18 6L6 18" />
        </svg>
      )}
    </span>
  );
}

const Ico = {
  Bell: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  ),
  Rocket: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  ),
  Clipboard: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect width="8" height="4" x="8" y="2" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M9 12h6" /><path d="M9 16h4" />
    </svg>
  ),
};

const FEATURES = [
  {
    id: 'f1',
    title: 'WhatsApp-Erinnerungen',
    body: 'Automatische Terminerinnerungen direkt an deine Kunden, 24h vorher.',
    eta: 'Q4 2026',
    status: 'In Planung',
  },
  {
    id: 'f2',
    title: 'Kunden-Warteliste',
    body: 'Freiwerdende Slots automatisch an wartende Kunden vergeben.',
    eta: 'In Prüfung',
    status: 'Ideenphase',
  },
  {
    id: 'f3',
    title: 'Team-Kalender Sync',
    body: 'Mehrere Mitarbeitende gleichzeitig, mit eigenen Farben und Rechten.',
    eta: '2027',
    status: 'Geplant',
  },
];

export default function InboxPopover({ inbox }: { inbox: AppointmentNotificationsState }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<InboxTab>('notifications');
  const rootRef = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, loaded, loadError, refresh: fetchNotifications, markRead } = inbox;

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => {
          if (!open) fetchNotifications();
          setOpen(v => !v);
        }}
        className="relative w-10 h-10 sm:w-[34px] sm:h-[34px] rounded-[10px] flex items-center justify-center transition-colors"
        style={{
          background: open ? 'rgba(255,109,0,0.12)' : '#F4F5FC',
          color: open ? '#FF6D00' : '#654F6F',
        }}
        aria-label="Benachrichtigungen"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {unreadCount > 0 && (
          <span
            className="absolute top-[7px] right-[7px] w-[7px] h-[7px] rounded-full bg-[#FF6D00]"
            style={{ border: '1.5px solid white' }}
          />
        )}
        <Ico.Bell />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-full mt-2 w-[min(392px,calc(100vw-24px))] origin-top-right z-50 overflow-hidden"
            style={{
              background: '#fff',
              border: '1px solid #E8EAF6',
              borderRadius: 18,
              boxShadow: '0 18px 48px rgba(14,11,30,0.14), 0 2px 8px rgba(14,11,30,0.04)',
            }}
            role="dialog"
            aria-label="Inbox"
          >
            <div
              className="h-[3px] w-full"
              style={{ background: 'linear-gradient(90deg, #FF6D00, #FF9A00 55%, #E8EAF6)' }}
            />

            <div className="px-4 pt-3.5 pb-3 flex items-start justify-between gap-3">
              <div>
                <div
                  className="text-[14px] font-bold text-[#0E0B1E] leading-tight"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  Inbox
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: '#654F6F' }}>
                  {unreadCount} ungelesen · News & Updates
                </div>
                {tab === 'notifications' && unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={() => markRead()}
                    className="text-[11px] font-bold mt-1 hover:underline"
                    style={{ color: '#FF6D00' }}
                  >
                    Alle als gelesen markieren
                  </button>
                )}
              </div>
              <div className="flex bg-[#F4F5FC] p-[3px] rounded-[10px] gap-0.5">
                {([
                  { id: 'notifications' as const, label: 'Meldungen', count: unreadCount },
                  { id: 'news' as const, label: 'News', count: 0 },
                ]).map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setTab(item.id)}
                    className="relative px-2.5 py-[5px] rounded-[7px] text-[11px] font-bold transition-colors"
                    style={
                      tab === item.id
                        ? { background: '#fff', color: '#0E0B1E', boxShadow: '0 1px 3px rgba(14,11,30,0.08)' }
                        : { color: '#654F6F' }
                    }
                  >
                    {item.label}
                    {item.count > 0 && (
                      <span
                        className="ml-1 inline-flex items-center justify-center min-w-[14px] h-[14px] px-1 rounded-full text-[9px] font-bold text-white"
                        style={{ background: '#FF6D00' }}
                      >
                        {item.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-px bg-[#E8EAF6]" />

            <AnimatePresence mode="wait">
              {tab === 'notifications' ? (
                <motion.div
                  key="notifications"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.16 }}
                  className="overflow-y-auto"
                  style={{ maxHeight: 420 }}
                >
                  {notifications.length === 0 ? (
                    <div className="px-6 py-10 text-center">
                      <div
                        className="w-10 h-10 mx-auto rounded-full flex items-center justify-center mb-2.5"
                        style={{ background: '#F4F5FC', color: '#654F6F' }}
                      >
                        <Ico.Bell />
                      </div>
                      <div
                        className="text-[12.5px] font-bold text-[#0E0B1E]"
                        style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                      >
                        {!loaded ? 'Lädt…' : loadError ? 'Meldungen nicht verfügbar' : 'Keine Meldungen'}
                      </div>
                      {loaded && (
                        <p className="text-[11.5px] mt-1" style={{ color: '#654F6F' }}>
                          {loadError
                            ? 'Bitte versuche es gleich noch einmal.'
                            : 'Neue Buchungen und Stornierungen erscheinen hier.'}
                        </p>
                      )}
                    </div>
                  ) : (
                    <ul className="py-1.5">
                      {notifications.map((item, index) => (
                        <li key={item.id} className="relative">
                          <button
                            type="button"
                            onClick={() => !item.read && markRead([item.id])}
                            className="group relative w-full flex items-start gap-3 pl-3 pr-4 py-3 text-left outline-none"
                            style={{ cursor: item.read ? 'default' : 'pointer' }}
                          >
                            <span
                              aria-hidden
                              className="absolute inset-x-1.5 inset-y-[3px] rounded-[10px] bg-[#F4F5FC] opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100 group-focus-visible:shadow-[inset_0_0_0_2px_rgba(255,109,0,0.4)]"
                            />
                            <span className="relative flex w-full items-start gap-3 transition-transform duration-150 ease-out group-active:scale-[0.99]">
                              <span className="w-1.5 h-7 flex items-center justify-center flex-shrink-0">
                                {!item.read && (
                                  <>
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#FF6D00]" />
                                    <span className="sr-only">Ungelesen</span>
                                  </>
                                )}
                              </span>
                              <span
                                className="transition-opacity duration-150"
                                style={{ opacity: item.read ? 0.55 : 1 }}
                              >
                                <InboxGlyph type={item.type} />
                              </span>
                              <span className="min-w-0 flex-1 pt-px">
                                <span className="flex items-baseline justify-between gap-3">
                                  <span
                                    className="text-[13px] leading-[1.3] font-bold"
                                    style={{
                                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                                      color: item.read ? '#654F6F' : '#0E0B1E',
                                    }}
                                  >
                                    {TOAST_VARIANTS[item.type].title}
                                  </span>
                                  <span
                                    className="text-[11px] tabular-nums flex-shrink-0"
                                    style={{ fontFamily: "'Inter', sans-serif", color: '#654F6F', opacity: 0.75 }}
                                  >
                                    {formatRelative(item.created_at)}
                                  </span>
                                </span>
                                <span
                                  className="block text-[12.5px] leading-[1.45] mt-0.5 break-words"
                                  style={{
                                    fontFamily: "'Inter', sans-serif",
                                    color: '#654F6F',
                                    opacity: item.read ? 0.8 : 1,
                                  }}
                                >
                                  {notificationSubline(item)}
                                </span>
                              </span>
                            </span>
                          </button>
                          {index < notifications.length - 1 && (
                            <div aria-hidden className="absolute bottom-0 left-[70px] right-4 h-px bg-[#E8EAF6]" />
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="news"
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.16 }}
                  className="overflow-y-auto px-4 py-3.5 space-y-4"
                  style={{ maxHeight: 420 }}
                >
                  <div>
                    <div className="flex items-center gap-1.5 mb-2.5">
                      <span style={{ color: '#FF6D00' }}><Ico.Rocket /></span>
                      <span
                        className="text-[10px] font-bold uppercase tracking-[0.12em]"
                        style={{ color: '#654F6F', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                      >
                        Geplante Features
                      </span>
                    </div>
                    <div className="space-y-2">
                      {FEATURES.map(feature => (
                        <div
                          key={feature.id}
                          className="rounded-[14px] px-3.5 py-3 border border-[#E8EAF6]"
                          style={{ background: '#FAFBFE' }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div
                              className="text-[12.5px] font-bold text-[#0E0B1E]"
                              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                            >
                              {feature.title}
                            </div>
                            <span
                              className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md flex-shrink-0"
                              style={{ background: 'rgba(255,109,0,0.10)', color: '#FF6D00' }}
                            >
                              {feature.status}
                            </span>
                          </div>
                          <p className="text-[11.5px] leading-relaxed mt-1" style={{ color: '#654F6F' }}>
                            {feature.body}
                          </p>
                          <div className="text-[10px] font-medium mt-1.5" style={{ color: '#9CA3AF' }}>
                            {feature.eta}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div
                    className="rounded-[16px] p-3.5 border border-[#E8EAF6] relative overflow-hidden"
                    style={{ background: 'linear-gradient(165deg, #FFF8F2 0%, #FFFFFF 48%)' }}
                  >
                    <div
                      className="absolute -top-8 -right-6 w-24 h-24 rounded-full pointer-events-none"
                      style={{ background: 'radial-gradient(circle, rgba(255,109,0,0.14), transparent 70%)' }}
                    />
                    <div className="flex items-center gap-2 mb-2 relative">
                      <div
                        className="w-7 h-7 rounded-[9px] flex items-center justify-center"
                        style={{ background: 'rgba(255,109,0,0.12)', color: '#FF6D00' }}
                      >
                        <Ico.Clipboard />
                      </div>
                      <div>
                        <div
                          className="text-[12.5px] font-bold text-[#0E0B1E] leading-tight"
                          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                        >
                          Monatsumfrage · August
                        </div>
                        <div className="text-[10px]" style={{ color: '#654F6F' }}>
                          Wünsche & Verbesserungsvorschläge
                        </div>
                      </div>
                    </div>

                    <p className="text-[11.5px] leading-relaxed mb-3 relative" style={{ color: '#654F6F' }}>
                      Was sollen wir als Nächstes bauen? Sag uns, was dir fehlt — oder was nervt.
                    </p>

                    <div className="space-y-2 relative">
                      <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>
                          Wunsch
                        </span>
                        <div
                          className="mt-1 rounded-[10px] px-3 py-2 text-[12px] border border-[#E8EAF6] bg-white"
                          style={{ color: '#9CA3AF' }}
                        >
                          z.B. Export als PDF…
                        </div>
                      </label>
                      <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>
                          Verbesserungsvorschlag
                        </span>
                        <div
                          className="mt-1 rounded-[10px] px-3 py-2 text-[12px] border border-[#E8EAF6] bg-white"
                          style={{ color: '#9CA3AF' }}
                        >
                          Was könnte einfacher laufen?
                        </div>
                      </label>
                    </div>

                    <button
                      type="button"
                      disabled
                      className="mt-3 w-full py-2 rounded-[10px] text-[12px] font-bold text-white cursor-not-allowed"
                      style={{
                        background: 'linear-gradient(135deg, #FF6D00, #FF9A00)',
                        opacity: 0.45,
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                      }}
                    >
                      Bald verfügbar
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
